/**
 * @file Central configuration for the SCR Weekly experience.
 *
 * Everything environment- or deployment-specific lives here so that pointing
 * the site at Backblaze (the real manifest host) or a live backend is a
 * one-file change.
 *
 * Overridable at build time via CRA env vars (must be prefixed REACT_APP_):
 *   REACT_APP_SCR_MANIFEST_BASE    -> base URL where current.json + archive/ live
 *   REACT_APP_SCR_UNITY_MANIFEST   -> exact URL the Unity build fetches (baked in)
 *   REACT_APP_SCR_API_BASE         -> future backend base URL (leaderboard, ...)
 *   REACT_APP_SCR_USE_MOCKS        -> "false" to hit the real backend
 */

const env =
  (typeof process !== 'undefined' && process.env) ? process.env : {};

/** Base path this experience is mounted under (see routes in App.js). */
export const BASE_PATH = '/scr-weekly';

/**
 * Base URL under which the generation pipeline publishes manifests + assets:
 *   {BASE}/current.json
 *   {BASE}/archive/index.json
 *   {BASE}/archive/s1-wNN.json
 *   {BASE}/s1-wNN/{skybox.jpg,music.mp3,loading.png,track.json}
 *
 * Production is Backblaze (the generation pipeline publishes here). Override
 * with REACT_APP_SCR_MANIFEST_BASE for a different bucket or for local testing
 * (e.g. "/scr-weekly" to use this app's own /public sample data).
 *
 * No trailing slash.
 * @type {string}
 */
export const MANIFEST_BASE_URL = (
  env.REACT_APP_SCR_MANIFEST_BASE ||
  'https://f005.backblazeb2.com/file/genblaze-hackathon'
).replace(/\/$/, '');

/** The current week's manifest — the single source of truth for "this week". */
export const CURRENT_MANIFEST_URL = `${MANIFEST_BASE_URL}/current.json`;

/** The archive index — the single source of truth for past competitions. */
export const ARCHIVE_INDEX_URL = `${MANIFEST_BASE_URL}/archive/index.json`;

/**
 * The EXACT url the shipped Unity build fetches for its weekly manifest. This is
 * baked into the build (ThisWeekConfig.ManifestUrl) and cannot be changed
 * without rebuilding Unity — so, to play archived weeks, the web layer
 * intercepts requests to this url and redirects them (see unityManifestRouter).
 *
 * Defaults to the known baked value; override if the build changes.
 * @type {string}
 */
export const UNITY_WEEKLY_MANIFEST_URL =
  env.REACT_APP_SCR_UNITY_MANIFEST ||
  'https://f005.backblazeb2.com/file/genblaze-hackathon/current.json';

/**
 * Resolve an archive index entry to an absolute manifest URL under the manifest
 * base. Tolerates entries whose `manifestUrl` is absolute (http), root-relative
 * (`/scr-weekly/archive/x.json`), or bucket-relative (`archive/x.json`), and
 * falls back to the conventional `{BASE}/archive/{id}.json`.
 *
 * @param {{ id?: string, manifestUrl?: string }} entry
 * @returns {string}
 */
export function resolveArchiveManifestUrl(entry) {
  if (entry && typeof entry.manifestUrl === 'string' && entry.manifestUrl) {
    const raw = entry.manifestUrl;
    if (/^https?:\/\//i.test(raw)) return raw;
    // Use just the file name so any prefix style maps onto the manifest base.
    const fileName = raw.split('/').filter(Boolean).pop();
    if (fileName) return `${MANIFEST_BASE_URL}/archive/${fileName}`;
  }
  if (entry && entry.id) return `${MANIFEST_BASE_URL}/archive/${entry.id}.json`;
  throw new Error('Cannot resolve archive manifest URL: entry has no id/manifestUrl.');
}

/**
 * Future backend base URL for the leaderboard. Only used once mocks are off.
 * @type {string}
 */
export const API_BASE = env.REACT_APP_SCR_API_BASE || '';

/** While true, LeaderboardService reads local sample data instead of a backend. */
export const USE_MOCKS = env.REACT_APP_SCR_USE_MOCKS !== 'false';

/** Local sample endpoint for the (mock) current-week leaderboard. */
export const MOCK_ENDPOINTS = {
  leaderboard: `${BASE_PATH}/leaderboard.json`,
};

/** Future backend endpoints, resolved lazily so they can be swapped later. */
export const API_ENDPOINTS = {
  leaderboard: (season, week) =>
    `${API_BASE}/api/scr-weekly/leaderboard?season=${season}&week=${week}`,
};

/** Network timeout (ms) applied to every request. */
export const REQUEST_TIMEOUT_MS = 10000;

/** Build internal route paths in one place. */
export const routes = {
  current: BASE_PATH,
  archive: `${BASE_PATH}/archive`,
  leaderboard: `${BASE_PATH}/leaderboard`,
  /** Play a specific archived competition by its id, e.g. "s1-w29". */
  play: (id) => `${BASE_PATH}/play/${id}`,
};
